import * as constants from "../constants";
import axios from "axios";
import { ENGINE_OVERRIDE } from "../helpers/engineSelector";
import { TEST_MODE } from "../helpers/testMode";

import {
  IdentifiedImagePayload,
  ImageSearchResponse,
} from "../classes/searchResponse";
import * as collectionCache from "../helpers/collectionCache";

class SearchRequestService {
  /** Retrieves the art information for the provided image id */
  getArtworkInformation = async (imageId, lang) => {
    // Fast path: English visitors of permanent-collection objects get the
    // snapshot from collectionCache (in-memory after Home pre-warmed it).
    // No Lambda round trip. Other languages still go through the server
    // since shortDescription is translated per request by AWS Translate.
    if (!lang || lang === "En") {
      // A failed snapshot download (flaky connection) rejects here; treat it as a
      // miss and use the server, instead of leaving the page on its skeleton.
      let cached;
      try {
        cached = await collectionCache.get(String(imageId));
      } catch {
        cached = undefined;
      }
      if (cached) {
        // FOC-239: the snapshot has no content blocks (or anything else only the
        // server knows), so the page renders this instantly and then enriches
        // itself from the server (ArtworkWrapper → getArtworkFromServer). That
        // server fetch also covers the legacy app-server scan record this path
        // used to fire separately. (Recap bookmarks are written by the engine on
        // each resolved scan via X-Focus-Session-Id, not by either call.)
        return {
          data: {
            records: [cached],
            roomRecords: null,
            message: "Result found",
            showStory: false,
            specialExhibition: false,
            fromSnapshot: true,
          },
          success: true,
          requestComplete: true,
        };
      }
    }

    return this.getArtworkFromServer(imageId, lang);
  };

  /** The full artwork record from the server (content blocks, translations, and
   *  anything else the in-browser snapshot lacks). */
  getArtworkFromServer = async (imageId, lang) => {
    try {
      let response = await axios.get(constants.ART_WORK_INFO_URL + imageId, {
        params: { lang },
      });
      return response.data;
    } catch (error) {
      console.log(
        "An error occurred while retrieving the artwork information from the server"
      );
    }
  };

  /** Stores the search attempt in the server */
  storeSearchedResult = async (searchResponse) => {
    const {
      searchSuccess,
      referenceImageUrl,
      esResponse,
      searchTime,
      data: formData,
    } = searchResponse;

    formData.append("searchSuccess", searchSuccess);
    formData.append("referenceImageUrl", referenceImageUrl);
    formData.append("esResponse", JSON.stringify(esResponse));
    formData.append("searchTime", searchTime);

    await axios.post(constants.STORE_SEARCHED_RESULT_URL, formData);
  };

  submitBookmarksEmail = async (email) => {
    const payload = { email };
    //payload.language = localStorage.getItem(SNAP_LANGUAGE_PREFERENCE) || 'en';
    await axios.post(constants.SUBMIT_BOOKMARKS_EMAIL_URL, payload);
  };

  /**
   * FOC-143: once-only email ingest to the new engine recap pipeline. Posts the
   * visit session_id + email to the Express proxy (/api/email/ingest → engine
   * /v1/email), which idempotently upserts session_id→email. Fire-and-forget,
   * dual-written alongside the legacy submitBookmarksEmail during the strangler
   * transition (FOC-138). Never blocks or throws into the UI.
   */
  submitEmailIngest = async (sessionId: string, email: string) => {
    try {
      // FOC-138 localization hook: send the visitor's current language so the
      // engine stores it on the session's rows and the lifted recap localizes
      // (last-submit-wins — mirrors the legacy job's most-recent-bookmark
      // language). Lowercased to the engine's shortcodes; 'en' when unset.
      const language = (
        localStorage.getItem(constants.SNAP_LANGUAGE_PREFERENCE) || "en"
      ).toLowerCase();
      await axios.post(constants.EMAIL_INGEST_URL, {
        session_id: sessionId,
        email,
        language,
      });
    } catch {
      // best-effort — the recap email is a nicety, never worth surfacing
    }
  };

  /**
   * Records a scanned permanent-collection artwork so a later "email my scans"
   * submit has a bookmark row to attach to. Needed because the collectionCache
   * fast path in getArtworkInformation skips the server route that normally
   * records this. Fire-and-forget — best-effort, never blocks or throws into
   * the render path (the recap email is a nicety, not worth failing a scan).
   */
  recordScannedArtwork = (imageId) => {
    axios.post(constants.RECORD_SCAN_URL, { imageId }).catch(() => {
      /* best-effort; never surface to the visitor */
    });
  };

  saveLanguagePreference = async (language) => {
    const payload = { language };
    await axios.post(constants.SAVE_LANGUAGE_PREFERENCE_URL, payload);
  };

  getAppTranslations = async (lang) => {
    try {
      let response = await axios.get(constants.APP_TRANSLATIONS_URL, {
        params: { lang },
      });
      return response.data.data.translations;
    } catch (error) {
      console.log(
        "An error occurred while retrieving the translations from the server"
      );
    }
  };

  getStoryItems = async (imageId, lang) => {
    try {
      let response = await axios.get(constants.STORIES_URL(imageId), {
        params: { lang },
      });
      return response.data;
    } catch (error) {
      console.log("An error occurred while retrieving story from the server");
    }
  };

  getStoriesFromEmail = async (slug, lang) => {
    try {
      let response = await axios.get(
        constants.STORIES_EMAIL_PAGE_URL + slug + "?lang=" + lang
      );
      return response.data;
    } catch (error) {
      console.log("An error occurred while retrieving story from the server");
    }
  };

  markStoryAsRead = async (imageId: string, storyId: string) => {
    try {
      let response = await axios.post(
        constants.STORIES_READ_URL(imageId, storyId)
      );
      return response.data;
    } catch (error) {
      console.log(
        "An error occurred while marking story as read from the server"
      );
    }
  };

  /** Submits the image search request to the backend and returns and ImageSearchResponse object */
  public async submitImageSearchRequest(
    imageBlob: Blob,
    batchId?: string,
    sessionId?: string,
    lastScanAt?: number
  ) {
    try {
      const formData = new FormData();
      formData.set("image", imageBlob, "temp_image.jpg");

      const headers: Record<string, string> = {};
      if (batchId) {
        headers["X-Focus-Batch-Id"] = batchId;
      }
      // FOC-136: the visit session_id groups a visit's scans (recap grouping +
      // the once-only email ingest + retention); last-scan-at is a session idle
      // signal. Forwarded to the engine by scanController, like X-Focus-Batch-Id.
      if (sessionId) {
        headers["X-Focus-Session-Id"] = sessionId;
      }
      if (lastScanAt) {
        headers["X-Focus-Last-Scan-At"] = String(lastScanAt);
      }
      // FOC-152: QA/test scans carry X-Focus-Test so the engine excludes them
      // from the adjudication queue + stats. Only set in QA mode (?test=1);
      // never for a normal visitor. scanController forwards it to the engine.
      if (TEST_MODE) {
        headers["X-Focus-Test"] = "1";
      }

      // Pass through ?engine= from the persisted selector (set via URL,
      // stored in localStorage — see helpers/engineSelector.ts). The
      // server proxy forwards to the engine's /v2/query?engine=...
      // selector. Lets us A/B engines (orb vs global vs sp_fallback)
      // without redeploying. With no override the engine uses its own
      // default (currently global).
      const requestUrl = ENGINE_OVERRIDE
        ? `${constants.SCAN_SEARCH_URL}?engine=${encodeURIComponent(ENGINE_OVERRIDE)}`
        : constants.SCAN_SEARCH_URL;

      const response = await axios.post(requestUrl, formData, { headers });

      // Get the search time and number of results
      const searchTime = response.data.search_time;
      const resultsCount = response.data.results.length;

      // If a matching result was found, this image search was successful
      if (resultsCount > 0) {
        return new ImageSearchResponse(true, response, searchTime);
      }

      // Else, this image search was not successful
      else {
        return new ImageSearchResponse(false, response, searchTime);
      }
    } catch (error) {
      console.error(`An error occurred with the image search request`, error);
      return new ImageSearchResponse(false, null, null);
    }
  }

  /** Retrieves special exhibition object details from Hygraph/GraphCMS */
  getSpecialExhibitionObject = async (objectId, lang) => {
    try {
      const response = await axios.get(
        constants.EXHIBITION_OBJECT_URL(objectId),
        { params: { lang } }
      );
      return response.data;
    } catch (error) {
      console.log("An error occurred while retrieving story from the server");
    }
  };

  /** Closure function so that an identified item is processed only once. Returns an IdentifiedImagePayload object */
  processIdentifiedItem = async (identifiedItem, lang) => {
    // Get the image id and reference image url
    const imageId = identifiedItem.item.name;
    const referenceImageUrl = identifiedItem.image.thumb_120;
    let data;

    // If it is a special exhibition object, get info from CMS
    if (
      imageId.split("_")[0] === "SPEX" ||
      imageId.substr(0, 2).toLowerCase() === "ex"
    ) {
      const id =
        imageId.split("_")[0] === "SPEX"
          ? imageId.split("_")[1]
          : imageId.toUpperCase();

      data = await this.getSpecialExhibitionObject(id, lang);
      // Otherwise search for item in ElasticSearch
    }
    // Retrieve artwork information
    else {
      // Get the image ID from the ID'ed item
      if (imageId.includes("__REF_")) {
        const id = imageId.split("__")[0];
        data = await this.getArtworkInformation(id, lang);
      } else {
        data = await this.getArtworkInformation(imageId, lang);
      }
    }

    return new IdentifiedImagePayload(data, referenceImageUrl);
  };

  validateEmail = async (email) => {
    try {
      let response = await axios.post(constants.VALIDATE_EMAIL_URL, {
        email: email,
      });
      return response.data;
    } catch (error) {
      console.log("An error occurred while validating email");
    }
  };

  public async getBookmarkEmail(sessionId: string) {
    try {
      const response = await axios.get(constants.BOOKMARK_EMAIL_URL(sessionId));
      return response.data;
    } catch (e) {
      console.log(
        "An error occurred while retrieving bookmark email from the server"
      );
    }
  }

  public async getStoryEmail(sessionId: string) {
    try {
      const response = await axios.get(constants.STORY_EMAIL_URL(sessionId));
      return response.data;
    } catch (e) {
      console.log(
        "An error occurred while retrieving story email from the server"
      );
    }
  }
}

export { SearchRequestService };
